import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Table, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Card, 
  Row, 
  Col,
  Tooltip,
  Popconfirm,
  message,
  InputNumber,
  Badge,
  Statistic,
  Calendar,
  Typography,
  Divider,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  FileExcelOutlined,
  LockOutlined,
  UnlockOutlined
} from '@ant-design/icons';
import moment from 'moment';
import {
  getPlanesMensuales,
  getPlanMensualById,
  createPlanMensual,
  updateActividadDiaria,
  cerrarPlanMensual,
  deletePlanMensual,
  getEstadisticasPlanMensual,
  getVistaCalendario,
  getUsuarios,
  getActividades
} from '../services/api';
import NavigationBar from '../components/layout/Navbar';
import ExportPlanMensualExcel from '../components/export/ExportPlanMensualExcel';
import '../assets/styles/DatabaseStyles.css';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// Mapeo de centros de producción (esto debería venir de la base de datos)
const CENTROS_PRODUCCION = {
  1: 'Buenos Aires',
  2: 'Santa Fe', 
  3: 'Córdoba',
  4: 'Entre Ríos',
  5: 'La Pampa'
};

const MESES = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

const PlanMensual = () => {
  // Estados principales
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [calendarioVisible, setCalendarioVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [calendarioData, setCalendarioData] = useState(null);
  const [form] = Form.useForm();
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    año: new Date().getFullYear(),
    mes: new Date().getMonth() + 1
  });
  
  // Estados para datos de soporte
  const [usuarios, setUsuarios] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [estadisticas, setEstadisticas] = useState([]);
  
  // Estados para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Estados para actividades del plan
  const [planActividades, setPlanActividades] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchPlanes();
    fetchEstadisticas();
  }, [pagination.current, pagination.pageSize, filtros]);

  const fetchInitialData = async () => {
    try {
      const [usuariosData, actividadesData] = await Promise.all([
        getUsuarios(),
        getActividades()
      ]);
      
      setUsuarios(usuariosData);
      setActividades(actividadesData);
    } catch (error) {
      message.error('Error al cargar datos iniciales');
    }
  };

  const fetchPlanes = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filtros
      };
      
      const response = await getPlanesMensuales(params);
      setPlanes(response.planes);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('Error al cargar planes mensuales');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstadisticas = async () => {
    try {
      const estadisticasData = await getEstadisticasPlanMensual(filtros);
      setEstadisticas(estadisticasData);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const handleCreatePlan = async (values) => {
    try {
      const planData = {
        titulo: `Plan ${MESES.find(m => m.value === values.mes)?.label} ${values.año} - ${CENTROS_PRODUCCION[values.id_cp]}`,
        mes: values.mes,
        año: values.año,
        id_cp: values.id_cp,
        responsable_centro_id: values.responsable_centro_id,
        observaciones_generales: values.observaciones_generales,
        actividades_diarias: planActividades
      };

      await createPlanMensual(planData);
      message.success('Plan mensual creado exitosamente');
      
      setModalVisible(false);
      setPlanActividades([]);
      form.resetFields();
      fetchPlanes();
      fetchEstadisticas();
    } catch (error) {
      message.error('Error al crear plan mensual');
    }
  };

  const handleViewCalendario = async (plan) => {
    try {
      const response = await getVistaCalendario(plan.id);
      setCalendarioData(response);
      setSelectedPlan(plan);
      setCalendarioVisible(true);
    } catch (error) {
      message.error('Error al cargar vista de calendario');
    }
  };

  const handleUpdateActividad = async (planId, actividadId, dia, jornalesEjecutados) => {
    try {
      await updateActividadDiaria(planId, actividadId, dia, {
        jornales_ejecutados: jornalesEjecutados,
        completado: jornalesEjecutados > 0
      });
      
      message.success('Actividad actualizada');
      
      // Refrescar vista de calendario
      const response = await getVistaCalendario(planId);
      setCalendarioData(response);
      
      fetchPlanes();
    } catch (error) {
      message.error('Error al actualizar actividad');
    }
  };

  const handleCerrarPlan = async (planId, observaciones) => {
    try {
      await cerrarPlanMensual(planId, observaciones);
      message.success('Plan mensual cerrado exitosamente');
      fetchPlanes();
      fetchEstadisticas();
    } catch (error) {
      message.error('Error al cerrar plan mensual');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePlanMensual(id);
      message.success('Plan mensual eliminado exitosamente');
      fetchPlanes();
      fetchEstadisticas();
    } catch (error) {
      message.error('Error al eliminar plan mensual');
    }
  };

  const addActividad = () => {
    setPlanActividades([...planActividades, {
      actividad_id: '',
      dia_mes: 1,
      jornales_planificados: 0,
      meta_mensual: 0,
      unidad_medida: 'jornales',
      observaciones: ''
    }]);
  };

  const removeActividad = (index) => {
    const newActividades = planActividades.filter((_, i) => i !== index);
    setPlanActividades(newActividades);
  };

  const updateActividad = (index, field, value) => {
    const newActividades = [...planActividades];
    newActividades[index] = { ...newActividades[index], [field]: value };
    setPlanActividades(newActividades);
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'planificacion': 'blue',
      'aprobado': 'green',
      'en_ejecucion': 'orange',
      'cerrado': 'gray'
    };
    return colors[estado] || 'default';
  };

  const getEstadoIcon = (estado) => {
    const icons = {
      'planificacion': <EditOutlined />,
      'aprobado': <CheckCircleOutlined />,
      'en_ejecucion': <ClockCircleOutlined />,
      'cerrado': <LockOutlined />
    };
    return icons[estado] || <ClockCircleOutlined />;
  };

  const columns = [
    {
      title: 'Plan',
      dataIndex: 'titulo',
      key: 'titulo',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {MESES.find(m => m.value === record.mes)?.label} {record.año} - {CENTROS_PRODUCCION[record.id_cp]}
          </div>
        </div>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 120,
      render: (estado) => (
        <Tag color={getEstadoColor(estado)} icon={getEstadoIcon(estado)}>
          {estado.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Planificación', value: 'planificacion' },
        { text: 'Aprobado', value: 'aprobado' },
        { text: 'En Ejecución', value: 'en_ejecucion' },
        { text: 'Cerrado', value: 'cerrado' }
      ]
    },
    {
      title: 'Centro',
      dataIndex: 'id_cp',
      key: 'centro',
      width: 120,
      render: (id_cp) => (
        <Tag color="blue">{CENTROS_PRODUCCION[id_cp]}</Tag>
      ),
      filters: Object.entries(CENTROS_PRODUCCION).map(([id, nombre]) => ({
        text: nombre,
        value: parseInt(id)
      }))
    },
    {
      title: 'Responsable',
      dataIndex: ['responsableCentro', 'nombre'],
      key: 'responsable',
      width: 150,
      render: (_, record) => (
        <div>
          {record.responsableCentro?.nombre} {record.responsableCentro?.apellido}
        </div>
      )
    },
    {
      title: 'Jornales',
      key: 'jornales',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <Text type="secondary">Planificados: </Text>
            <Text strong>{record.total_jornales_planificados}</Text>
          </div>
          <div style={{ fontSize: '12px' }}>
            <Text type="secondary">Ejecutados: </Text>
            <Text strong color={record.total_jornales_ejecutados > 0 ? 'success' : 'default'}>
              {record.total_jornales_ejecutados}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Progreso',
      key: 'progreso',
      width: 100,
      render: (_, record) => {
        const porcentaje = record.total_jornales_planificados > 0 
          ? Math.round((record.total_jornales_ejecutados / record.total_jornales_planificados) * 100)
          : 0;
        return (
          <div style={{ textAlign: 'center' }}>
            <Text strong style={{ 
              color: porcentaje >= 100 ? '#52c41a' : porcentaje >= 50 ? '#faad14' : '#ff4d4f'
            }}>
              {porcentaje}%
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver calendario">
            <Button 
              type="text" 
              icon={<CalendarOutlined />} 
              onClick={() => handleViewCalendario(record)}
            />
          </Tooltip>
          <Tooltip title="Ver detalles">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => {
                setSelectedPlan(record);
                // Aquí podrías abrir un modal de detalles
              }}
            />
          </Tooltip>
          {record.estado === 'en_ejecucion' && (
            <Tooltip title="Cerrar plan">
              <Popconfirm
                title="¿Cerrar este plan mensual?"
                description="Esta acción marcará el plan como cerrado."
                onConfirm={() => handleCerrarPlan(record.id, 'Plan cerrado desde interfaz')}
                okText="Sí"
                cancelText="No"
              >
                <Button 
                  type="text" 
                  icon={<LockOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          )}
          {record.estado === 'planificacion' && (
            <Tooltip title="Eliminar">
              <Popconfirm
                title="¿Estás seguro de eliminar este plan?"
                onConfirm={() => handleDelete(record.id)}
                okText="Sí"
                cancelText="No"
              >
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const renderEstadisticas = () => {
    const totalPlanes = estadisticas.reduce((sum, est) => sum + parseInt(est.cantidad_planes), 0);
    const planesEnEjecucion = estadisticas
      .filter(est => est.estado === 'en_ejecucion')
      .reduce((sum, est) => sum + parseInt(est.cantidad_planes), 0);
    
    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total de Planes"
              value={totalPlanes}
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="En Ejecución"
              value={planesEnEjecucion}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Jornales Planificados"
              value={estadisticas.reduce((sum, est) => sum + parseInt(est.total_planificados || 0), 0)}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Jornales Ejecutados"
              value={estadisticas.reduce((sum, est) => sum + parseInt(est.total_ejecutados || 0), 0)}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const renderCalendarioMensual = () => {
    if (!calendarioData) return null;

    const { plan, calendario } = calendarioData;
    const diasDelMes = Object.keys(calendario).length;

    // Crear estructura de tabla similar al Excel
    const actividadesUnicas = {};
    Object.values(calendario).forEach(dia => {
      dia.actividades.forEach(act => {
        if (!actividadesUnicas[act.actividad_id]) {
          actividadesUnicas[act.actividad_id] = {
            id: act.actividad_id,
            nombre: act.actividad?.nombre || 'Actividad',
            dias: {}
          };
        }
        actividadesUnicas[act.actividad_id].dias[dia.dia] = act;
      });
    });

    const columnasDias = [];
    for (let dia = 1; dia <= diasDelMes; dia++) {
      columnasDias.push({
        title: dia.toString(),
        dataIndex: ['dias', dia],
        key: `dia_${dia}`,
        width: 50,
        align: 'center',
        render: (actividad) => {
          if (!actividad) return '-';
          
          return (
            <div style={{ cursor: 'pointer' }}>
              <InputNumber
                size="small"
                min={0}
                value={actividad.jornales_ejecutados}
                placeholder={actividad.jornales_planificados.toString()}
                onChange={(value) => handleUpdateActividad(
                  plan.id, 
                  actividad.actividad_id, 
                  actividad.dia_mes, 
                  value || 0
                )}
                style={{ 
                  width: '40px',
                  backgroundColor: actividad.jornales_ejecutados > 0 ? '#f6ffed' : 'transparent'
                }}
              />
              <div style={{ fontSize: '10px', color: '#666' }}>
                /{actividad.jornales_planificados}
              </div>
            </div>
          );
        }
      });
    }

    const columnasActividades = [
      {
        title: 'Actividad',
        dataIndex: 'nombre',
        key: 'actividad',
        width: 200,
        fixed: 'left'
      },
      ...columnasDias,
      {
        title: 'Total',
        key: 'total',
        width: 80,
        fixed: 'right',
        render: (_, record) => {
          const totalEjecutado = Object.values(record.dias).reduce(
            (sum, act) => sum + (act?.jornales_ejecutados || 0), 0
          );
          const totalPlanificado = Object.values(record.dias).reduce(
            (sum, act) => sum + (act?.jornales_planificados || 0), 0
          );
          
          return (
            <div>
              <Text strong>{totalEjecutado}</Text>
              <div style={{ fontSize: '10px', color: '#666' }}>
                /{totalPlanificado}
              </div>
            </div>
          );
        }
      }
    ];

    return (
      <Modal
        title={`${plan.titulo} - Vista de Calendario`}
        visible={calendarioVisible}
        onCancel={() => setCalendarioVisible(false)}
        width={Math.min(1400, window.innerWidth * 0.9)}
        footer={[
          <Button key="close" onClick={() => setCalendarioVisible(false)}>
            Cerrar
          </Button>,
          <ExportPlanMensualExcel 
            key="export"
            planData={selectedPlan}
            calendarioData={calendarioData}
          />
        ]}
      >
        <Alert
          message="Vista de Planificación Mensual"
          description="Ingresa los jornales ejecutados en cada día. Los números pequeños muestran lo planificado."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Table
          columns={columnasActividades}
          dataSource={Object.values(actividadesUnicas)}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1200, y: 400 }}
          size="small"
          bordered
        />
      </Modal>
    );
  };

  return (
    <div>
      <NavigationBar />
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={2}>Plan de Trabajo Mensual</Title>
          <Text type="secondary">Gestiona la planificación mensual por centro de producción</Text>
        </div>

        {renderEstadisticas()}

        <Card>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => {
                  setPlanActividades([]);
                  form.resetFields();
                  setModalVisible(true);
                }}
              >
                Nuevo Plan Mensual
              </Button>
            </Space>

            <Space>
              <Select
                placeholder="Año"
                value={filtros.año}
                onChange={(value) => setFiltros(prev => ({ ...prev, año: value }))}
                style={{ width: 100 }}
              >
                {[2023, 2024, 2025, 2026].map(año => (
                  <Option key={año} value={año}>{año}</Option>
                ))}
              </Select>
              
              <Select
                placeholder="Mes"
                value={filtros.mes}
                onChange={(value) => setFiltros(prev => ({ ...prev, mes: value }))}
                style={{ width: 120 }}
              >
                {MESES.map(mes => (
                  <Option key={mes.value} value={mes.value}>{mes.label}</Option>
                ))}
              </Select>

              <Select
                placeholder="Centro"
                value={filtros.id_cp}
                onChange={(value) => setFiltros(prev => ({ ...prev, id_cp: value }))}
                style={{ width: 150 }}
                allowClear
              >
                {Object.entries(CENTROS_PRODUCCION).map(([id, nombre]) => (
                  <Option key={id} value={parseInt(id)}>{nombre}</Option>
                ))}
              </Select>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={planes}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} de ${total} planes`,
              onChange: (page, pageSize) => {
                setPagination(prev => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize
                }));
              }
            }}
            scroll={{ x: 1000 }}
          />
        </Card>

        {/* Modal para crear plan mensual */}
        <Modal
          title="Nuevo Plan Mensual"
          visible={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setPlanActividades([]);
            form.resetFields();
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreatePlan}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="mes"
                  label="Mes"
                  rules={[{ required: true, message: 'Selecciona el mes' }]}
                >
                  <Select placeholder="Seleccionar mes">
                    {MESES.map(mes => (
                      <Option key={mes.value} value={mes.value}>{mes.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="año"
                  label="Año"
                  rules={[{ required: true, message: 'Selecciona el año' }]}
                >
                  <Select placeholder="Seleccionar año">
                    {[2023, 2024, 2025, 2026].map(año => (
                      <Option key={año} value={año}>{año}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="id_cp"
                  label="Centro de Producción"
                  rules={[{ required: true, message: 'Selecciona el centro' }]}
                >
                  <Select placeholder="Seleccionar centro">
                    {Object.entries(CENTROS_PRODUCCION).map(([id, nombre]) => (
                      <Option key={id} value={parseInt(id)}>{nombre}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="responsable_centro_id"
              label="Responsable del Centro"
              rules={[{ required: true, message: 'Selecciona el responsable' }]}
            >
              <Select placeholder="Seleccionar responsable" showSearch>
                {usuarios.map(usuario => (
                  <Option key={usuario.id} value={usuario.id}>
                    {usuario.nombre} {usuario.apellido}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="observaciones_generales"
              label="Observaciones Generales"
            >
              <TextArea rows={3} placeholder="Observaciones del plan mensual" />
            </Form.Item>

            <Divider>Actividades Planificadas</Divider>
            
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" onClick={addActividad} block>
                <PlusOutlined /> Agregar Actividad
              </Button>
            </div>

            {planActividades.map((actividad, index) => (
              <Card key={index} size="small" style={{ marginBottom: 8 }}>
                <Row gutter={8} align="middle">
                  <Col span={6}>
                    <Select
                      placeholder="Actividad"
                      value={actividad.actividad_id}
                      onChange={(value) => updateActividad(index, 'actividad_id', value)}
                      style={{ width: '100%' }}
                    >
                      {actividades.map(act => (
                        <Option key={act.id} value={act.id}>
                          {act.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={3}>
                    <InputNumber
                      placeholder="Día"
                      min={1}
                      max={31}
                      value={actividad.dia_mes}
                      onChange={(value) => updateActividad(index, 'dia_mes', value)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={3}>
                    <InputNumber
                      placeholder="Jornales"
                      min={0}
                      value={actividad.jornales_planificados}
                      onChange={(value) => updateActividad(index, 'jornales_planificados', value)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={3}>
                    <InputNumber
                      placeholder="Meta"
                      min={0}
                      value={actividad.meta_mensual}
                      onChange={(value) => updateActividad(index, 'meta_mensual', value)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={3}>
                    <Select
                      placeholder="Unidad"
                      value={actividad.unidad_medida}
                      onChange={(value) => updateActividad(index, 'unidad_medida', value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="jornales">Jornales</Option>
                      <Option value="hectareas">Hectáreas</Option>
                      <Option value="litros">Litros</Option>
                      <Option value="unidades">Unidades</Option>
                    </Select>
                  </Col>
                  <Col span={4}>
                    <Input
                      placeholder="Observaciones"
                      value={actividad.observaciones}
                      onChange={(e) => updateActividad(index, 'observaciones', e.target.value)}
                    />
                  </Col>
                  <Col span={2}>
                    <Button 
                      type="text" 
                      danger 
                      icon={<DeleteOutlined />}
                      onClick={() => removeActividad(index)}
                    />
                  </Col>
                </Row>
              </Card>
            ))}

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setModalVisible(false);
                  setPlanActividades([]);
                  form.resetFields();
                }}>
                  Cancelar
                </Button>
                <Button type="primary" htmlType="submit">
                  Crear Plan Mensual
                </Button>
              </Space>
            </div>
          </Form>
        </Modal>

        {renderCalendarioMensual()}
      </div>
    </div>
  );
};

export default PlanMensual;
